import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ContactType,
  updateDownlineDetails,
  ValidationType,
  type Contact,
  type downlineAddressType,
} from '@commerce-profile-webcomponents/sdk';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { useTranslation, TextLink, TextLinkStyle, FAIcon, Theme, Shade, IconStyle } from '@nextgen-web-framework/all';
import DownlineAddressList from '../DownlineAddress/DownlineAddressList';
import EmptyAddressBook from '../EmptyAddressBook/EmptyAddressBook';
import { getDownlineAddressListUtil } from '../../../utils/contactUtil';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import AddressCard from './address-card/AddressCard';
import type { AddressListProps } from './types/propTypes';
import styles from './AddressList.module.scss';
import { DownlineTabs } from './DownlineTabs/DownlineTabs';

const AddressList = ({
  onAddressEdit,
  onAddressSelect,
  isSelectedAddress,
  addressList,
  showSkeletonLoader,
  downlineAddressList,
  setDownlineAddress,
  isDownlineAddressOpen,
  showHeading,
  allowEdit = true,
  allowSelectInvalidAddress = false,
  isProxyOrder,
  addressListOfProxy = [],
  allowEditOnDefault,
  defaultTagTheme,
}: AddressListProps) => {
  const t = useTranslation();
  const skeltonArr = [1, 2, 3, 4];
  const showEmptyAddress = isDownlineAddressOpen && addressList?.length === 0;
  const [viewAllAddress, setViewAllAddress] = useState(false);
  const selectedAddressRef = useRef<HTMLDivElement>(null);
  const [selectedDownlinerDetailsObj, setSelectedDownlinerDetailsObj] = useState<downlineAddressType>({
    firstName: '',
    lastName: '',
    accountId: '',
    ownerId: '',
  });
  const [isProxyDownlinerAddressLoading, setIsProxyDownlinerAddressLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const downlinerDetails = useSelector((state: AppState) => state.commerceProfile.downlineAddressDetails);
  const selectedTabRef = useRef(0);
  const apiErrorHandler = useGlobalErrorHandler();
  const undefinedCheckHandler = (expectedOutput: string | undefined, fallback: string) =>
    expectedOutput ?? fallback ?? '';

  const updateSelectedDownlinerDetailsWithTab = (downlinerObj: downlineAddressType, selectedTabNumber?: number) => {
    if (selectedTabNumber && !isNaN(selectedTabNumber)) {
      selectedTabRef.current = selectedTabNumber;
    }
    setSelectedDownlinerDetailsObj(downlinerObj);
  };

  const handleDownlinerClick = async (
    ownerId: string,
    firstName: string,
    lastName: string,
    accountId: string,
    coApplicantIds?: string[],
    coApplicantFname?: string,
    coApplicantLname?: string,
    needToUpdateDownlinerDetails = true,
  ) => {
    const contactType = ContactType.Shipping;
    setIsProxyDownlinerAddressLoading(true);
    const res = await getDownlineAddressListUtil(contactType, ownerId, accountId, apiErrorHandler);
    if (
      ((coApplicantIds?.length && !coApplicantIds.includes(ownerId)) || !coApplicantIds?.length) &&
      needToUpdateDownlinerDetails
    ) {
      updateSelectedDownlinerDetailsWithTab(
        { firstName, lastName, accountId, ownerId, coApplicantIds, coApplicantFname, coApplicantLname },
        0,
      );
    }
    setIsProxyDownlinerAddressLoading(false);
    if (res.contacts?.length === 0) {
      dispatch(
        updateDownlineDetails({
          ...downlinerDetails,
          downLinerOwnerId: ownerId,
        }),
      );
    }
    // Loop through each contact and add profileName
    const contactsWithProfileName = res.contacts.map((contact) => ({
      ...contact,
      profileName: `${firstName} ${lastName}`,
    }));
    setDownlineAddress?.(contactsWithProfileName ?? [], isDownlineAddressOpen);
    setDownlinerAddress(`${firstName} ${lastName}`);
  };

  let displayAddress: Contact[] = [];

  if (Array.isArray(addressList)) {
    let tempAddressList = addressList;

    if (downlineAddressList && !viewAllAddress && !isDownlineAddressOpen) {
      tempAddressList = addressList.slice(0, 1);
    }

    displayAddress = tempAddressList.filter((item) => item !== undefined);
  }

  const showViewAllAddess =
    !viewAllAddress && addressList && addressList.length > 0 && displayAddress?.length !== addressList.length;

  const handleTextLink = () => {
    setViewAllAddress(true);
  };

  const [downliner, setDownliner] = useState('');
  const setDownlinerAddress = (downlineName: string) => {
    setDownliner(downlineName);
  };

  useEffect(() => {
    if (selectedAddressRef.current && selectedAddressRef.current.scrollIntoView) {
      selectedAddressRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedAddressRef]);

  useEffect(() => {
    if (!showSkeletonLoader && isProxyOrder && downlineAddressList?.length) {
      const firstDownlineAddress = downlineAddressList?.[0];
      const downlinerObj: downlineAddressType = {
        firstName: firstDownlineAddress?.firstName,
        lastName: firstDownlineAddress?.lastName,
        accountId: firstDownlineAddress?.accountId,
        ownerId: firstDownlineAddress?.ownerId,
        coApplicantIds: firstDownlineAddress?.coApplicantIds,
        coApplicantFname: undefinedCheckHandler(
          firstDownlineAddress?.coApplicantFname,
          firstDownlineAddress?.firstName,
        ),
        coApplicantLname: undefinedCheckHandler(firstDownlineAddress?.coApplicantLname, firstDownlineAddress?.lastName),
      };

      if (
        isSelectedAddress?.ownerId &&
        firstDownlineAddress?.coApplicantIds?.length &&
        firstDownlineAddress?.coApplicantIds?.includes(isSelectedAddress?.ownerId)
      ) {
        updateSelectedDownlinerDetailsWithTab(downlinerObj, 1);
        handleDownlinerClick(
          firstDownlineAddress?.coApplicantIds?.[0],
          undefinedCheckHandler(firstDownlineAddress?.coApplicantFname, firstDownlineAddress?.firstName),
          undefinedCheckHandler(firstDownlineAddress?.coApplicantLname, firstDownlineAddress?.lastName),
          firstDownlineAddress?.accountId,
          firstDownlineAddress?.coApplicantIds,
          undefinedCheckHandler(firstDownlineAddress?.coApplicantFname, firstDownlineAddress?.firstName),
          undefinedCheckHandler(firstDownlineAddress?.coApplicantLname, firstDownlineAddress?.lastName),
        );
      } else {
        updateSelectedDownlinerDetailsWithTab(downlinerObj, 0);
        handleDownlinerClick(
          firstDownlineAddress?.ownerId,
          firstDownlineAddress?.firstName,
          firstDownlineAddress?.lastName,
          firstDownlineAddress?.accountId,
          firstDownlineAddress?.coApplicantIds,
          undefinedCheckHandler(firstDownlineAddress?.coApplicantFname, firstDownlineAddress?.firstName),
          undefinedCheckHandler(firstDownlineAddress?.coApplicantLname, firstDownlineAddress?.lastName),
          false,
        );
      }
    }
  }, [showSkeletonLoader]);

  const heading = (isForProxyUser?: boolean) =>
    downlineAddressList && (isDownlineAddressOpen || isForProxyUser)
      ? t('profile.AddressInput.addressBookName', { name: downliner })
      : t('profile.addressList.heading');

  const tabsdata = [
    {
      id: '0',
      title: t('profile.addressList.downlinerTabTitlePrimary'),
      profileId: selectedDownlinerDetailsObj?.ownerId,
      accountId: selectedDownlinerDetailsObj?.accountId,
      firstName: selectedDownlinerDetailsObj?.firstName,
      lastName: selectedDownlinerDetailsObj?.lastName,
    },
    ...(selectedDownlinerDetailsObj?.coApplicantIds?.[0]
      ? [
          {
            id: '1',
            title: t('profile.addressList.downlinerTabTitleCoApplicant'),
            profileId: selectedDownlinerDetailsObj?.coApplicantIds?.[0],
            accountId: selectedDownlinerDetailsObj?.accountId,
            firstName: undefinedCheckHandler(
              selectedDownlinerDetailsObj?.coApplicantFname,
              selectedDownlinerDetailsObj?.firstName,
            ),
            lastName: undefinedCheckHandler(
              selectedDownlinerDetailsObj?.coApplicantLname,
              selectedDownlinerDetailsObj?.lastName,
            ),
          },
        ]
      : []),
  ];

  const handleTabClick = (selectedTabIndex: number) => {
    selectedTabRef.current = selectedTabIndex;
    const selectedTabObj = tabsdata[selectedTabIndex];
    handleDownlinerClick(
      selectedTabObj.profileId,
      selectedTabObj?.firstName,
      selectedTabObj?.lastName,
      selectedTabObj.accountId,
      selectedDownlinerDetailsObj?.coApplicantIds,
      selectedDownlinerDetailsObj?.coApplicantFname,
      selectedDownlinerDetailsObj?.coApplicantLname,
    );
  };

  const renderAddressCards = (
    addresses: Contact[],
    allowSelectInvalidAddressFlag: boolean,
    allowEditFlag?: boolean,
  ) => (
    <>
      {addresses &&
        addresses.map((userAddress: Contact, index) => {
          const selectedAddress = userAddress?.contactId === isSelectedAddress?.contactId;
          const className = `${styles['card']} ${index === addresses.length - 1 ? styles['last-card'] : ''}`;
          return (
            <div ref={selectedAddress ? selectedAddressRef : null} key={userAddress?.contactId} className={className}>
              <AddressCard
                key={userAddress?.contactId}
                selectedAddress={selectedAddress}
                onAddressSelect={onAddressSelect}
                userAddress={userAddress}
                onAddressEdit={onAddressEdit}
                isDownlineAddressOpen={isDownlineAddressOpen}
                allowEdit={(allowEditFlag && userAddress?.validationStatus == ValidationType.NotValidated) ?? allowEdit}
                allowSelectInvalidAddress={allowSelectInvalidAddressFlag}
                allowEditOnDefault={allowEditOnDefault}
                defaultTagTheme={defaultTagTheme}
              />
            </div>
          );
        })}
    </>
  );

  const skeltonLoader = skeltonArr.map((index) => (
    <div className={styles['skeleton-loader-wrapper']} key={index}>
      <div className={styles['skeleton-loader-container']}>
        <div className={styles['skeleton-loader']}></div>
        <div className={styles['skeleton-loader']}></div>
        <div className={styles['skeleton-loader']}></div>
      </div>
    </div>
  ));

  return (
    <div className={`${styles['address-list-container']}`} data-testid="address-list-testid">
      {showHeading && <div className={styles['address-list-heading']}>{heading()}</div>}

      {showEmptyAddress ? (
        <EmptyAddressBook />
      ) : (
        <div className={styles['address-list-card-container']}>
          {isDownlineAddressOpen && (
            <DownlineTabs
              tabsData={tabsdata}
              ontabChange={(selectedTabId) => {
                handleTabClick(selectedTabId);
              }}
              selectedTabNumber={selectedTabRef.current}
            />
          )}
          {renderAddressCards(displayAddress, allowSelectInvalidAddress)}
          {!showSkeletonLoader && !isDownlineAddressOpen && (
            <div className={styles['address-list-downline-container']}>
              {showViewAllAddess && (
                <div className={styles['address-list-textlink-container']}>
                  <span className={styles['address-list-textlink']}>
                    <TextLink
                      id="view-more-address"
                      data-testid="view-more-address"
                      text={t('profile.addressList.textLink')}
                      textLinkStyle={TextLinkStyle.UNDERLINE}
                      onClick={handleTextLink}
                      href="#"
                    />
                  </span>
                  <span>
                    <FAIcon
                      theme={Theme.BLACK}
                      iconName={'chevron-down'}
                      shade={Shade.DARK}
                      iconStyle={IconStyle.SOLID}
                    />
                  </span>
                </div>
              )}
              {isProxyOrder ? (
                <div className={styles['proxy-downliner-address-list']}>
                  <div className={styles['address-list-heading']}>{downliner && heading(isProxyOrder)}</div>
                  <DownlineTabs
                    tabsData={tabsdata}
                    ontabChange={(selectedTabId) => {
                      handleTabClick(selectedTabId);
                    }}
                    selectedTabNumber={selectedTabRef.current}
                  />
                  {isProxyDownlinerAddressLoading ? (
                    skeltonLoader
                  ) : (
                    <div className={styles['proxy-address-list-card-container']}>
                      {/* true: 2nd arg to make the address card selectable even it is invalid address. (not editable) */}
                      {/* 2025-06-16: change bad address to not selectable */}
                      {renderAddressCards(addressListOfProxy, true, true)}
                    </div>
                  )}
                </div>
              ) : (
                <DownlineAddressList
                  downlineAddressList={downlineAddressList as downlineAddressType[]}
                  isSelectedAddress={isSelectedAddress}
                  handleDownlinerClick={handleDownlinerClick}
                  setSelectedDownlinerDetails={updateSelectedDownlinerDetailsWithTab}
                />
              )}
            </div>
          )}

          {showSkeletonLoader && skeltonLoader}
        </div>
      )}
    </div>
  );
};
export default AddressList;
